<template>
    <div class="timetable-free-places">
        <div class="container" v-if="renderComponent">
            <div class="row">
                <div class="table-cell col-sm-3 cell-time">Data / Ora</div>
                <div class="table-cell col-sm-9">Instructor</div>
            </div>
            <template v-for="date in dates">
                <div class="row">
                    <div class="p-3 col-sm-12 date-line pointer" @click.stop.prevent="toggleDate(date)">
                        {{ date | format_date("dddd, D MMMM YYYY") }}
                        <span class="group-icon">{{ showDate[date] ? "-" : "+" }}</span>
                    </div>

                    <div v-if="loadingEvents" class="my-4 text-center col-sm-12">
                        <atom-spinner class="navi-spinner" :animation-duration="1000" :size="30"
                            :color="'#ff1d5e'" />
                    </div>
                    <div class="date-events col-sm-12"
                        v-bind:class="{ hide: loadingEvents || !showDate[date] || !freePlacesByDate[date] }">
                        <div v-if="freePlacesByDate[date] && !freePlacesByDate[date].length" class="row">
                            <div class="table-cell col-sm-12">Nu sunt locuri libere</div>
                        </div>
                        <div v-for="(event, index) in freePlacesByDate[date]"
                            @click="clickFreeEventTemplate(event, date)" class="row pointer hover:bg-slate-200"
                            v-bind:class="{ 'table-success': !!event._created_event }">
                            <div class="table-cell col-sm-3 cell-time">
                                {{ event.start_time | format_time }} -
                                {{ event.end_time | format_time }}
                            </div>
                            <div class="table-cell col-sm-9">
                                {{ event.calendar_name }}
                                <span class="badge badge-primary" v-if="event._created_event">
                                    {{ event._created_event.title }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import TimetableViewBase from "./TimetableView-Base.vue";
import { loading } from "./mixins/LoadingMixin";
import { EventDatesMixin } from "./mixins/EventDatesMixin";

export default {
    extends: TimetableViewBase,
    mixins: [loading, EventDatesMixin],

    props: {
        freeEventsUrl: {
            default: "",
        },
        selectedCalendar: {
            default: null,
        },
        selectedGearboxType: {
            default: null,
        },
        selectedCarModel: {
            default: null,
        },
    },

    data() {
        return {
            view: "free_places",
            requestVerificationCode: 0,
            eventLists: [],
            freePlacesByDate: {},
            showDate: {},
            loadingEvents: false,
            renderComponent: true,
        };
    },

    mounted() {
        console.log("Timetable View Free Places Component Mounted.");
        this.getFreeEventsBetweenDates();

        this.eventBus.$on("reload-calendar", this.getFreeEventsBetweenDates);
        this.eventBus.$on("deleted-event", this.forceRerender);
        this.eventBus.$on("created-event", this.forceRerender);
        this.eventBus.$on("view-changed", this.reloadOnFreePlacesView);

        // this.eventBus.$on("selected-calendar", this.getFreeEventsBetweenDates);

        this.initDates();
    },

    // The bus outlives this component, which is mounted under v-if and so is
    // recreated on every switch into the free-places view. Without this, each
    // destroyed instance stays subscribed and fires its own request forever.
    beforeDestroy() {
        this.eventBus.$off("reload-calendar", this.getFreeEventsBetweenDates);
        this.eventBus.$off("deleted-event", this.forceRerender);
        this.eventBus.$off("created-event", this.forceRerender);
        this.eventBus.$off("view-changed", this.reloadOnFreePlacesView);
    },

    methods: {
        reloadOnFreePlacesView(view) {
            if (view === "free_places") {
                this.getFreeEventsBetweenDates();
            }
        },

        getFreeEventsBetweenDates() {
            if (!this.freeEventsUrl) return Promise.resolve();

            // Verification code is used to know with which response to populate data events
            let _requestVerificationCode = ++this.requestVerificationCode;
            this.loadingEvents = true;

            this.lastRequestedEventsStartDate = moment(this.viewStartDate);
            this.lastRequestedEventsEndDate = moment(this.viewEndDate);

            return axios
                .get(this.freeEventsUrl, {
                    params: {
                        start_date: this.lastRequestedEventsStartDate.toISOString(),
                        end_date: this.lastRequestedEventsEndDate.toISOString(),
                        calendar_id: this.selectedCalendar?.id,
                        gearbox_type: this.selectedGearboxType?.value,
                        car_model_id: this.selectedCarModel?.value,
                    },
                })
                .then((response) => {
                    // Check if this is the last request
                    if (_requestVerificationCode !== this.requestVerificationCode) return;

                    this.freePlacesByDate = {};

                    _.each(response.data, (eventList) => {
                        _.each(eventList.events, this.fixEventData);
                        _.each(eventList.event_templates, this.fixEventData);

                        _.each(this.dates, (date) => {
                            let dateKey = date.unix();

                            let events = this.filterEventListByDate(eventList.events, date);
                            let event_templates = this.filterEventListByDate(
                                eventList.event_templates,
                                date
                            );

                            // Hide past free events
                            if (this.hidePastEvents) {
                                event_templates = event_templates.filter((event) => {
                                    return !this.isEventTemplateInPast(event, date);
                                });
                            }

                            let free_event_templates = this.filterFreeEventTemplates(
                                event_templates,
                                events,
                                eventList.block_events_after_hour
                            );

                            _.each(free_event_templates, (event_template) => {
                                event_template.calendar_id = eventList.calendar_id;
                                event_template.calendar_name = eventList.calendar_name;
                                event_template.store_events_url = eventList.store_events_url;
                            });

                            this.freePlacesByDate[date] = this.freePlacesByDate[date] || [];
                            this.freePlacesByDate[date] =
                                this.freePlacesByDate[date].concat(free_event_templates);
                        });
                    });

                    _.each(this.dates, (date) => {
                        let prevEvent = {};
                        this.freePlacesByDate[date] = _.sortBy(
                            this.freePlacesByDate[date],
                            "_start_on_minute"
                        ).filter((event) => {
                            if (prevEvent.calendar_id == event.calendar_id
                                && event._start_on_minute < prevEvent._end_on_minute) {
                                return false;
                            }

                            prevEvent = event;

                            return true;
                        });
                    });

                    this.eventLists = response.data;

                    this.loadingEvents = false;
                })
                .catch((error) => {
                    // Mirror the success path's guard: a superseded request must not clear
                    // the spinner, because the newer request it lost to is still running.
                    if (_requestVerificationCode !== this.requestVerificationCode) return;

                    this.loadingEvents = false;

                    this.$notify({
                        group: "app",
                        text: error?.response?.data?.message || 'A apărut o eroare. Încercați din nou.',
                        type: "error",
                    });
                });
        },

        toggleDate(date) {
            this.showDate[date] = !this.showDate[date];
            // this.forceRerender();
            this.$forceUpdate();
        },

        initDates() {
            _.each(this.dates, (date) => {
                this.showDate[date] =
                    typeof this.showDate[date] == "undefined"
                        ? true
                        : this.showDate[date];
            });
        },

        loadEventsStepByStep() { },

        filterEventListByDate(events, date) {
            if (!moment(date).isValid()) return [];

            return events.filter((event) => {
                if (event.is_recurring) {
                    return event._recurrence && event._recurrence.matches(date);
                } else {
                    return event._start_time.isSame(date, "day");
                }
            });
        },

        filterFreeEventTemplates(
            event_templates,
            events,
            block_events_after_hour,
            block_past_events
        ) {
            return event_templates
                .filter((event_template) => {
                    if (event_template._start_hour >= block_events_after_hour) {
                        return false;
                    }

                    for (let i = 0; i < events.length; i++) {
                        let event = events[i];

                        if (
                            (event_template._start_on_minute <= event._start_on_minute &&
                                event._start_on_minute < event_template._end_on_minute) ||
                            (event_template._start_on_minute < event._end_on_minute &&
                                event._end_on_minute <= event_template._end_on_minute) ||
                            (event._start_on_minute <= event_template._start_on_minute &&
                                event_template._end_on_minute <= event._end_on_minute)
                        ) {
                            return false;
                        }
                    }
                    return true;
                })
                .map((e) => {
                    // Copy all templates
                    return { ...e };
                });
        },

        isEventTemplateInPast(event, date) {
            let start_time = moment(date)
                .hour(event._start_time.hour())
                .minute(event._start_time.minute());

            return start_time < this.now;
        },

        forceRerender() {
            // Remove my-component from the DOM
            this.renderComponent = false;

            this.$nextTick(() => {
                // Add the component back in
                this.renderComponent = true;
            });
        },
    },

    watch: {
        viewStartDate() {
            this.getFreeEventsBetweenDates();
            this.initDates();
        },

        selectedCalendar() {
            this.getFreeEventsBetweenDates();
        },

        selectedGearboxType() {
            this.getFreeEventsBetweenDates();
        },

        selectedCarModel() {
            this.getFreeEventsBetweenDates();
        },
    },

    computed: {
        now() {
            return moment();
        },
    },
};
</script>
<style lang="scss">
.timetable-free-places {
    padding-left: 10px;
    /* border: 1px solid; */
    display: inline-block;
    width: 100%;

    .container {
        width: 100%;
        display: inline-block;
        border: 1px solid #c8c8c8;
    }

    .row {
        border-bottom: 1px solid #c8c8c8;
        // border-left: 1px solid #c8c8c8;
    }

    .pointer {
        cursor: pointer;
    }

    .table-cell {
        padding: 10px 15px;
    }

    .cell-time {
        min-width: 130px;
    }

    .date-line {
        background-color: #eaeaea;
    }

    // .date-events {
    //     border-left: 3px solid #c8c8c8;
    // }

    .hide {
        display: none;
    }

    .group-icon {
        background-color: transparent;
        font-size: 20px;
        padding: 6px;
        line-height: 10px;
        float: right;
    }
}
</style>
